import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDown, ChevronRight } from "untitledui-js-base";
import { Show } from "shared/components";
import {
  PopoverClose,
  PopoverDescription,
  PopoverHeading
} from "shared/components/Popover";
import { useAuthContext } from "providers/Auth";
import { formatPhoneDisplay } from "views/phone-numbers/lib/format";
import { AiLeasingAgentVisibility } from "types/landlord/address";
import { getPopoverCopy } from "./copy";

const MAX_LISTED_NUMBERS = 3;

const ACTION_CLASS =
  "-ml-2 mt-1 flex h-8 items-center self-start rounded-lg px-2 text-xs font-bold text-primary-500 hover:bg-primary-20";

// Text and icon share one inline-flex box so they centre on the same axis
// instead of the icon sitting on the text baseline.
const ActionLabel = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex items-center gap-1">{children}</span>
);

const PhoneNumberList = ({
  numbers
}: {
  numbers: AiLeasingAgentVisibility["phone_numbers"];
}) => {
  const shown = numbers.slice(0, MAX_LISTED_NUMBERS);
  const rest = numbers.length - shown.length;

  return (
    <ul className="mt-1 divide-y divide-alpha-80 overflow-hidden rounded-lg border border-alpha-100">
      {shown.map((number) => (
        <li
          key={number.id}
          className="flex min-h-10 items-center justify-between gap-3 px-3 text-xs"
        >
          <span className="shrink-0 whitespace-nowrap font-medium tabular-nums text-content-1000">
            {formatPhoneDisplay(number.phone_number)}
          </span>
          <span className="min-w-0 truncate text-content-400">
            {number.name}
          </span>
        </li>
      ))}
      <Show if={rest > 0}>
        <li className="flex min-h-10 items-center bg-background-900 px-3 text-xs text-content-400">
          +{rest} more
        </li>
      </Show>
    </ul>
  );
};

/**
 * Explains the field's state and links to where it can be fixed. Read-only:
 * nothing here changes data.
 */
export const VisibilityPopover = ({
  visibility,
  propertyName,
  unitListed,
  onGoToListUnit
}: {
  visibility: AiLeasingAgentVisibility;
  propertyName: string;
  unitListed: boolean;
  onGoToListUnit: () => void;
}) => {
  const navigate = useNavigate();
  const { permissions } = useAuthContext();
  const copy = getPopoverCopy(visibility, propertyName, { unitListed });

  if (!copy) return null;

  const { action } = copy;
  const canFollow =
    action.kind === "list_unit" ||
    !action.settings ||
    permissions.canAccessUsersSettings;

  return (
    <div className="w-full space-y-2 rounded-lg border border-alpha-100 bg-white p-4 text-left shadow-xl">
      <PopoverHeading className="text-sm font-bold leading-5 text-content-1000">
        {copy.title}
      </PopoverHeading>
      <PopoverDescription className="text-xs font-normal leading-[18px] text-content-500">
        {copy.body}
      </PopoverDescription>
      <Show if={visibility.phone_numbers.length > 0}>
        <PhoneNumberList numbers={visibility.phone_numbers} />
      </Show>
      <Show if={canFollow}>
        {action.kind === "list_unit" ? (
          <PopoverClose className={ACTION_CLASS} onClick={onGoToListUnit}>
            <ActionLabel>
              {action.label}
              <ArrowDown size="14" className="stroke-primary-500" aria-hidden />
            </ActionLabel>
          </PopoverClose>
        ) : (
          <PopoverClose
            className={ACTION_CLASS}
            onClick={() => navigate(action.to)}
          >
            <ActionLabel>
              {action.label}
              <ChevronRight
                size="14"
                className="stroke-primary-500"
                aria-hidden
              />
            </ActionLabel>
          </PopoverClose>
        )}
      </Show>
    </div>
  );
};
